<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import axios from 'axios';
import { QuizterBuilder, useQuizterLoader, useQuizterStore } from '@ilteducation/games';
import type { SelectedVideo } from '@ilteducation/games';
import type { CloseEndedQuestion } from '@ilteducation/exercise-service-types';
import { useLocale, useLocalisation } from '@/plugins/translation';
import useSnackbar from '@/composables/use-snackbar';

// Load Quizter translations from the Quizter Lokalise project
const { includeAdditionalProject } = useLocalisation();
includeAdditionalProject('Quizter', false);

const { t } = useLocale();
const route = useRoute();
const router = useRouter();
const quizterStore = useQuizterStore();
const { loadQuizter } = useQuizterLoader();
const { snackbarError } = useSnackbar();

const justSaved = window.history.state?.justSaved === true;

const activityId = computed(() => route.params.activityId as string);

const gamesPath = router.resolve({ name: 'Games' }).href;
const absoluteGamesUrl = `${window.location.origin}${gamesPath}`;

const returnParams = computed(() => ({
  url: window.location.href,
  text: t('quiz.back'),
}));

const returnParamsPlayer = computed(() => ({
  url: absoluteGamesUrl,
  text: t('quizter.back_to_games'),
}));

// Resolve the existing quizter into the builder's initial state.
const initialVideos = ref<SelectedVideo[]>([]);
const initialName = ref('');
const initialDescription = ref('');
const loading = ref(false);

onMounted(async () => {
  loading.value = true;
  try {
    const quizter = await loadQuizter(activityId.value);
    initialVideos.value = quizter.videos;
    initialName.value = quizter.name;
    initialDescription.value = quizter.description;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      snackbarError(t('quizter.not_found'));
      router.push({ name: 'QuizterHomePage' });
    } else {
      snackbarError(t('quizter.load_error'));
    }
  } finally {
    loading.value = false;
  }
});

const goBack = () => {
  router.push({ name: 'QuizterMyPage' });
};

const handleStart = async (resourceIds: string[], questions?: CloseEndedQuestion[]) => {
  if (resourceIds.length === 0) {
    return;
  }
  await quizterStore.createGame(
    resourceIds,
    false,
    activityId.value,
    questions,
    returnParams.value,
    returnParamsPlayer.value,
  );
  const gameId = quizterStore.gameId;
  if (gameId) {
    router.push({ name: 'HostQuizter', params: { gameId } });
  }
};

const handleSave = async ({
  resourceIds,
  name,
  description,
  questions,
}: {
  resourceIds: string[];
  name: string;
  description?: string;
  questions?: CloseEndedQuestion[];
}) => {
  await quizterStore.updateQuizter(activityId.value, { resourceIds, name, description, questions });
};
</script>

<template>
  <QuizterBuilder
    :initial-videos="initialVideos"
    :initial-name="initialName"
    :initial-description="initialDescription"
    :loading="loading"
    :saved="justSaved"
    :start="handleStart"
    :save="handleSave"
    @back="goBack"
  />
</template>

<style scoped lang="scss"></style>
